'use strict'

export default class TableColumn {
    static JEXL_COMPILATION_CACHE = {};

    /**
     * @param {string} name
     * @param {string} objectExpressionString
     * @param {{type?:string, name?:string, width?:string, autoDetectType?:boolean}?} displayOptions
     */
    constructor(name, objectExpressionString, displayOptions) {
        this._name = name;
        this._expr = TableColumn.getJexlExpression(objectExpressionString);

        this._dependencies = {
            names: TableColumn.parseColumnNames(objectExpressionString),
            missingNames: []
        };

        this._displayOptions = Object.assign({
            type: COLUMN_TYPE.UNKNOWN,
            name: name,
            width: '140px',
            // This should solve the problem when we have numeric column which may have null values.
            // E.g. peak.base, when peak is created on non-MS chromatogram or chromatogram w/o spectra.
            autoDetectType: true,
            customProp: false
        }, displayOptions);
    }

    evaluate(injection) {
        const object = this._expr.evalSync(injection);
        if(object === null || object === undefined)
            return null;
        return object;
    }

    getDefaultName(){
        return this._name;
    }

    getDisplayName(){
        return this._displayOptions.name;
    }

    getType(){
        return this._displayOptions.type;
    }

    /** @param {string} type */
    setType(type){
        this._displayOptions.type= type;
    }

    getExpression(){
        return this._expr._exprStr;
    }

    getRelatedColumns(){
        return this._dependencies.names;
    }

    /** @param {string[]} dependencies */
    setMissingDependencies(dependencies){
        this._dependencies.missingNames = dependencies;
    }

    /** @returns {string[]} */
    getMissingDependencies(){
        return this._dependencies.missingNames;
    }

    getDisplayOptions(){
        return this._displayOptions
    }

    isColumnTypeAutoDetectionEnabled() {
        return this._displayOptions.autoDetectType;
    }

    /** @returns {boolean} */
    isCustomColumn(){
        return this._displayOptions.customProp
    }

    toServerJson(){
        return {
            name: this.getDisplayName(),
            expr: this.getExpression(),
            type: this.getType(),
            width: this.getDisplayOptions().width,
            autoDetectType: this.isColumnTypeAutoDetectionEnabled()
        }
    }

    static parseServerJson(json){
        return new TableColumn(json.name, json.expr, {type:json.type, width:json.width || '140px', autoDetectType:json.autoDetectType})
    }

    /**
     * @module jexl
     * @param {string} exprString
     * @return {*}
     */
    static getJexlExpression(exprString) {
        return TableColumn.JEXL_COMPILATION_CACHE[exprString]
        || (TableColumn.JEXL_COMPILATION_CACHE[exprString] = jexl.compile(exprString))
    }

    static parseColumnNames(columnExpression){
        return [...columnExpression.matchAll(/col\[['"]([^'"]*)['"]]/g)].map(m => m[1])
    }

    static assertExpressionDoesNotThrows(exprString) {
        if (typeof exprString !== "string" || exprString.length === 0)
            throw new Error("Expression is not a string or is empty");
        // Evaluate expression against object which have minimal set of required properties
        TableColumn.getJexlExpression(exprString).evalSync({col: {}, injection: {prop: {}}});
    }
}

export const COLUMN_TYPE = {
    UNKNOWN:    "unknown",
    NUMBERS:    "number",
    STRINGS:    "string",
    SVG:        "svg",
    MIXED:      "mixed",

    resolve: (value) => {
        if (COLUMN_TYPE._NULLABLE_VALUES.has(value))
            return COLUMN_TYPE.UNKNOWN
        if (typeof value === "number")
            return COLUMN_TYPE.NUMBERS
        return COLUMN_TYPE.STRINGS
    },

    isSvgExpression: (expression) => {
        return COLUMN_TYPE._SVG_REGEXP.test(expression)
    },

    _NULLABLE_VALUES: new Set([null, undefined, "", NaN, -Infinity, Infinity]),
    _SVG_REGEXP: new RegExp(`^substance(s\\[\\.displayName.*]\\[0])?\\.structure$`)
};